import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Input, Select, Table, StatusBadge, Badge } from '@/components/ui';
import { Plus, Search, Film, ExternalLink, Trash2, Download } from 'lucide-react';
import { format } from 'date-fns';

interface Movie {
  id: string;
  title: string;
  slug: string;
  posterUrl?: string;
  releaseDate?: string;
  runtime?: number;
  rating?: string;
  status: string;
  isNowPlaying: boolean;
  isComingSoon: boolean;
  showtimeCount?: number;
  createdAt: string;
}

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const displayOptions = [
  { value: '', label: 'All Movies' },
  { value: 'now-playing', label: 'Now Playing' },
  { value: 'coming-soon', label: 'Coming Soon' },
];

export function MoviesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [displayFilter, setDisplayFilter] = useState('');

  const { data: movies, isLoading } = useQuery<Movie[]>({
    queryKey: ['movies', { search, status: statusFilter, display: displayFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (displayFilter === 'now-playing') params.set('nowPlaying', 'true');
      if (displayFilter === 'coming-soon') params.set('comingSoon', 'true');
      const response = await api.get(`/admin/movies?${params}`);
      return response.data.data.items;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/movies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movies'] });
    },
  });

  const columns = [
    {
      key: 'poster',
      header: '',
      className: 'w-16',
      render: (movie: Movie) => (
        <div className="w-12 h-16 rounded overflow-hidden bg-midnight-100">
          {movie.posterUrl ? (
            <img
              src={movie.posterUrl}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film className="h-5 w-5 text-gray-600" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (movie: Movie) => (
        <div>
          <p className="font-medium text-white">{movie.title}</p>
          <div className="flex items-center gap-2 mt-1">
            {movie.rating && (
              <Badge variant="default">{movie.rating}</Badge>
            )}
            {movie.runtime && (
              <span className="text-xs text-gray-500">{movie.runtime} min</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (movie: Movie) => <StatusBadge status={movie.status} />,
    },
    {
      key: 'display',
      header: 'Display',
      render: (movie: Movie) => (
        <div className="flex flex-col gap-1">
          {movie.isNowPlaying && <Badge variant="success">Now Playing</Badge>}
          {movie.isComingSoon && <Badge variant="info">Coming Soon</Badge>}
          {!movie.isNowPlaying && !movie.isComingSoon && (
            <span className="text-sm text-gray-500">Hidden</span>
          )}
        </div>
      ),
    },
    {
      key: 'showtimes',
      header: 'Showtimes',
      render: (movie: Movie) => (
        <span className="text-gray-300">{movie.showtimeCount ?? 0}</span>
      ),
    },
    {
      key: 'releaseDate',
      header: 'Release Date',
      render: (movie: Movie) =>
        movie.releaseDate ? (
          <span className="text-gray-300">
            {format(new Date(movie.releaseDate), 'MMM d, yyyy')}
          </span>
        ) : (
          <span className="text-gray-500">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (movie: Movie) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/movies/${movie.id}`}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Are you sure you want to delete this movie?')) {
                deleteMutation.mutate(movie.id);
              }
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-midnight-100"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Movies</h1>
          <p className="text-gray-400 mt-1">Manage your movie catalog</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/movies/import">
            <Button variant="secondary">
              <Download className="h-4 w-4 mr-2" />
              Import from OMDB
            </Button>
          </Link>
          <Link to="/movies/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Movie
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search movies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="w-full md:w-48">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <div className="w-full md:w-48">
            <Select
              options={displayOptions}
              value={displayFilter}
              onChange={(e) => setDisplayFilter(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Movies Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={movies ?? []}
          keyField="id"
          loading={isLoading}
          onRowClick={(movie) => navigate(`/movies/${movie.id}`)}
          emptyMessage="No movies found"
        />
      </Card>
    </div>
  );
}
